<template>
  <div v-if="visible" class="modal-overlay" @click.self="fechar">
    <div class="modal-card">
      <div class="modal-header">
        <div class="avatar" :style="{ background: corAvatar }">
          {{ iniciais(membro?.nome) }}
        </div>
        <div class="flex-1">
          <h3>
            {{ membro?.nome }}
            <span class="tag-id" v-if="membro?.id">ID #{{ membro.id }}</span>
          </h3>
          <span class="tag-tipo">{{ membro?.tipoOrigem }}</span>
          <span :class="['tag-status', `status-${(membro?.status || '').toLowerCase()}`]">
            {{ membro?.status }}
          </span>
        </div>
        <button class="btn-fechar" @click="fechar" title="Fechar">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div v-if="loading" class="loading">
        <span class="material-icons">hourglass_empty</span> Carregando...
      </div>

      <div v-else class="modal-body">
        <div class="secao">
          <h4><span class="material-icons">contact_mail</span> Contato</h4>
          <div class="linha">
            <span class="material-icons mini">email</span>
            <a :href="`mailto:${membro.email}`">{{ membro.email || '—' }}</a>
          </div>
          <div class="linha">
            <span class="material-icons mini">call</span>
            <a :href="`tel:${membro.telefone}`">{{ membro.telefone || '—' }}</a>
          </div>
          <div class="linha" v-if="membro.telefone">
            <span class="material-icons mini" style="color: #22c55e;">chat</span>
            <a :href="linkWhatsapp" target="_blank" rel="noopener" class="link-whats">
              Abrir no WhatsApp
            </a>
          </div>
        </div>

        <div class="secao">
          <h4><span class="material-icons">badge</span> Dados</h4>
          <div class="grid-dados">
            <div><label>CPF</label><strong>{{ membro.cpf || '—' }}</strong></div>
            <div><label>Nascimento</label><strong>{{ formatarData(membro.dataNascimento) }}</strong></div>
            <div><label>Código indicação</label><code>{{ membro.codigoIndicacao }}</code></div>
            <div><label>Laravel User ID</label><strong>{{ membro.laravelUserId }}</strong></div>
            <div v-if="membro.coachIdLaravel"><label>Coach ID</label><strong>{{ membro.coachIdLaravel }}</strong></div>
            <div v-if="membro.studentIdLaravel"><label>Student ID</label><strong>{{ membro.studentIdLaravel }}</strong></div>
            <div><label>Ativado em</label><strong>{{ formatarDataHora(membro.ativadoEm) }}</strong></div>
            <div><label>Cadastro</label><strong>{{ formatarDataHora(membro.createdAt) }}</strong></div>
          </div>
        </div>

        <div class="secao" v-if="noGenealogia">
          <h4><span class="material-icons">account_tree</span> Posição na Rede</h4>
          <div class="grid-dados">
            <div><label>Nível</label><strong>{{ noGenealogia.nivel }}</strong></div>
            <div><label>Patrocinador</label><strong>{{ noGenealogia.nomePatrocinador || '— (raiz)' }}</strong></div>
            <div><label>Indicados diretos</label><strong>{{ diretos }}</strong></div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <router-link
          v-if="membro"
          :to="`/rede-marketing/rede-linear/${membro.id}`"
          class="btn-secondary"
          @click="fechar"
        >
          <span class="material-icons">list_alt</span>
          Rede linear
        </router-link>
        <router-link
          v-if="membro"
          :to="`/rede-marketing/genealogia/${membro.id}`"
          class="btn-primary"
          @click="fechar"
        >
          <span class="material-icons">account_tree</span>
          Ver árvore
        </router-link>
        <button class="btn-secondary" @click="fechar">Fechar</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import redeMarketingApi from '@/services/api/redeMarketingRepository'

const props = defineProps({
  visible: { type: Boolean, default: false },
  membroId: { type: Number, default: null },
})
const emit = defineEmits(['fechar'])

const membro = ref(null)
const noGenealogia = ref(null)
const diretos = ref(0)
const loading = ref(false)

function fechar() {
  emit('fechar')
}

function iniciais(nome) {
  if (!nome) return '?'
  const p = nome.trim().split(/\s+/)
  return (p.length === 1 ? p[0].substring(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase()
}

const corAvatar = computed(() => {
  if (!membro.value) return '#4f46e5'
  const cores = ['#4f46e5', '#7c3aed', '#F7941D', '#22c55e', '#ec4899', '#06b6d4']
  return cores[(membro.value.id || 0) % cores.length]
})

const linkWhatsapp = computed(() => {
  if (!membro.value?.telefone) return '#'
  const numero = membro.value.telefone.replace(/\D/g, '')
  const comDdi = numero.startsWith('55') ? numero : `55${numero}`
  return `https://wa.me/${comDdi}`
})

function formatarData(valor) {
  if (!valor) return '—'
  const d = new Date(valor)
  if (isNaN(d)) return valor
  return d.toLocaleDateString('pt-BR')
}

function formatarDataHora(valor) {
  if (!valor) return '—'
  const d = new Date(valor)
  if (isNaN(d)) return valor
  return d.toLocaleString('pt-BR')
}

async function carregar() {
  if (!props.membroId) return
  loading.value = true
  try {
    const membroResp = await redeMarketingApi.BuscarMembro(props.membroId)
    membro.value = membroResp.data

    try {
      const noResp = await redeMarketingApi.BuscarNo(props.membroId)
      noGenealogia.value = noResp.data
    } catch {
      noGenealogia.value = null
    }

    try {
      const diretosResp = await redeMarketingApi.ContarDiretos(props.membroId)
      diretos.value = diretosResp.data
    } catch {
      diretos.value = 0
    }
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.visible, props.membroId],
  ([vis, id]) => {
    if (vis && id) carregar()
  },
)
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); z-index: 1000;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.modal-card {
  background: #fff; border-radius: 14px; width: 100%; max-width: 620px;
  max-height: 92vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideIn 0.2s ease;
}
@keyframes slideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.modal-header {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 22px; border-bottom: 1px solid #e2e8f0;
}
.modal-header h3 { margin: 0 0 6px 0; color: #2D3A7C; font-size: 1.2rem; }
.flex-1 { flex: 1; }

.avatar {
  width: 54px; height: 54px; border-radius: 50%; display: flex;
  align-items: center; justify-content: center; color: #fff;
  font-weight: 700; font-size: 1.1rem; flex-shrink: 0;
}
.tag-id {
  background: #f1f5f9; color: #64748b; padding: 2px 8px; border-radius: 4px;
  font-size: 0.72rem; font-weight: 600; margin-left: 6px;
  font-family: monospace; vertical-align: middle;
}
.tag-tipo {
  background: #e0e7ff; color: #4338ca; padding: 2px 8px; border-radius: 4px;
  font-size: 0.75rem; font-weight: 600; margin-right: 6px;
}
.tag-status {
  padding: 2px 10px; border-radius: 10px; font-size: 0.75rem; font-weight: 600;
}
.status-ativo { background: #d1fae5; color: #065f46; }
.status-pendente { background: #fef3c7; color: #92400e; }
.status-suspenso { background: #fee2e2; color: #991b1b; }
.status-cancelado { background: #e5e7eb; color: #4b5563; }

.btn-fechar {
  background: transparent; border: none; cursor: pointer; color: #64748b;
  display: flex; padding: 4px;
}
.btn-fechar:hover { color: #ef4444; }

.loading { padding: 40px; text-align: center; color: #64748b; }

.modal-body { padding: 18px 22px; }
.secao { margin-bottom: 18px; }
.secao h4 {
  font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.3px;
  color: #64748b; display: flex; align-items: center; gap: 6px; margin: 0 0 8px 0;
}
.secao h4 .material-icons { font-size: 18px; color: #4f46e5; }

.linha {
  display: flex; align-items: center; gap: 10px; padding: 6px 0;
  font-size: 0.92rem;
}
.linha .mini { font-size: 16px; color: #94a3b8; }
.linha a { color: #4f46e5; text-decoration: none; }
.linha a:hover { text-decoration: underline; }
.link-whats { color: #22c55e !important; font-weight: 600; }

.grid-dados {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px;
}
.grid-dados > div { display: flex; flex-direction: column; }
.grid-dados label {
  font-size: 0.72rem; color: #94a3b8; text-transform: uppercase;
  letter-spacing: 0.3px; margin-bottom: 2px;
}
.grid-dados strong { color: #334155; font-size: 0.92rem; }
.grid-dados code {
  background: #f1f5f9; padding: 2px 6px; border-radius: 4px;
  font-size: 0.82rem; color: #334155; align-self: flex-start;
}

.modal-footer {
  padding: 14px 22px; border-top: 1px solid #e2e8f0; background: #fafbfc;
  display: flex; justify-content: flex-end; gap: 10px;
}
.btn-primary, .btn-secondary {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px;
  border-radius: 8px; font-weight: 600; cursor: pointer; text-decoration: none;
  border: none; font-size: 0.88rem;
}
.btn-primary { background: #4f46e5; color: #fff; }
.btn-primary:hover { background: #4338ca; }
.btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #334155; }
.btn-secondary:hover { background: #f8fafc; }
</style>
